import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { Amplify } from 'aws-amplify'
import { resetPassword, confirmResetPassword, updatePassword, fetchAuthSession } from 'aws-amplify/auth'
import amplifyConfig from '../services/amplify-config'
import api, { setAccessToken, getAccessToken, setAuthCallbacks } from '../services/api'
import { recordActivity, clearLastActivity, isIdleExpired, fetchSessionTimeoutConfig } from '../services/sessionActivity'
import { queryClient } from '../lib/react-query'
import {
  applyTenantClaimsToUser,
  fetchMembershipsForUser,
} from './authTenantHelpers'
import { decodeBase64Url } from '../utils/base64url'

// Keep Amplify configured for forgotPassword / confirmForgotPassword / changeUserPassword
// (password reset flows still use Amplify SDK; everything else goes through our Lambda proxy)
Amplify.configure(amplifyConfig)

const AuthContext = createContext(null)

// MFA challenge session state (passed through login challenge flow)
let currentChallengeSession = null

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [memberships, setMemberships] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false)
  const [requiresMfaVerification, setRequiresMfaVerification] = useState(false)
  const [mfaChallengeUser, setMfaChallengeUser] = useState(null)
  const [isAuthenticating, setIsAuthenticating] = useState(false)

  // Ref to the latest idToken so refreshSession / proactive refresh can re-read claims
  // without race-conditioning the setAccessToken in api.js.
  const idTokenRef = useRef(null)

  /**
   * Adopt a freshly-issued idToken: cache it locally for claim re-reads,
   * fold `activeTenantId` onto the user, fetch the membership list, and
   * commit the assembled user object to state.
   */
  const adoptSession = useCallback(async (incomingUser, idToken) => {
    idTokenRef.current = idToken || null
    const enriched = applyTenantClaimsToUser(incomingUser, idToken)
    if (!enriched) {
      setMemberships([])
      return null
    }
    const list = await fetchMembershipsForUser(api)
    setMemberships(list)
    const userWithMemberships = { ...enriched, memberships: list }
    setUser(userWithMemberships)
    return userWithMemberships
  }, [])

  // Cognito access token stored in a ref — only needed for logout GlobalSignOut.
  // API Gateway authorization uses the ID token (stored via setAccessToken).
  const cognitoAccessTokenRef = useRef(null)

  // Timer ref for proactive token refresh (fires 5 min before expiry).
  const refreshTimerRef = useRef(null)

  // ============================================
  // Proactive token refresh — fires 5 minutes before the ID token expires.
  // This avoids the CORS problem where API Gateway's 401 GatewayResponse
  // returns Access-Control-Allow-Origin: * which browsers block when
  // withCredentials: true is set, preventing the 401 refresh interceptor
  // from triggering at all.
  // ============================================

  const scheduleTokenRefresh = useCallback((idToken) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
    if (!idToken) return

    let delayMs
    try {
      const payload = JSON.parse(decodeBase64Url(idToken.split('.')[1]))
      delayMs = (payload.exp * 1000) - Date.now() - (5 * 60 * 1000) // 5 min before expiry
    } catch {
      return // Unparseable token — skip scheduling
    }
    if (delayMs <= 0) return

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const response = await api.post('/auth/refresh', {})
        const { user: refreshedUser, accessToken, idToken: newIdToken } = response.data
        setAccessToken(newIdToken)
        cognitoAccessTokenRef.current = accessToken
        if (refreshedUser) await adoptSession(refreshedUser, newIdToken)
        scheduleTokenRefresh(newIdToken)
      } catch {
        // Refresh failed — session expired; next API call will handle the 401
      }
    }, delayMs)
  }, [adoptSession])

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [])

  // ============================================
  // Token refresh callbacks for api.js
  // ============================================

  const handleTokenRefreshed = useCallback(({ user: refreshedUser, accessToken, idToken }) => {
    setAccessToken(idToken)
    cognitoAccessTokenRef.current = accessToken
    scheduleTokenRefresh(idToken)
    if (refreshedUser) {
      adoptSession(refreshedUser, idToken)
    } else {
      idTokenRef.current = idToken || null
    }
  }, [scheduleTokenRefresh, adoptSession])

  const handleSessionExpired = useCallback(() => {
    setUser(null)
    setProfile(null)
    setMemberships([])
    idTokenRef.current = null
    setError(null)
    queryClient.clear()
    sessionStorage.removeItem('coformit_mfa_session_check')
  }, [])

  // Register api.js callbacks once on mount
  useEffect(() => {
    setAuthCallbacks(handleTokenRefreshed, handleSessionExpired)
  }, [handleTokenRefreshed, handleSessionExpired])

  // ============================================
  // On mount: try to restore session via refresh token cookie
  // ============================================

  useEffect(() => {
    checkUser()
  }, [])

  const fetchProfile = async () => {
    try {
      const response = await api.get('/users/me')
      setProfile(response.data)
      return response.data
    } catch (err) {
      console.error('Failed to fetch profile:', err)
      setProfile(null)
      return null
    }
  }

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/users/me', profileData)
      const updatedProfile = response.data
      setProfile(updatedProfile)
      return updatedProfile
    } catch (err) {
      console.error('Failed to update profile:', err)
      throw new Error(err.message || 'Failed to update profile')
    }
  }

  /**
   * Try to restore the session from the HttpOnly refresh token cookie.
   * Called on mount and after SSO callback.
   */
  const checkUser = async () => {
    // E2E test mode: use user from localStorage instead of Lambda
    if (import.meta.env.VITE_E2E_AUTH) {
      const stored = localStorage.getItem('e2e_auth_user')
      if (stored) {
        const parsedUser = JSON.parse(stored)
        // #49: parity with the normal auth path — fetch memberships so the
        // TenantSwitcher and other membership-aware UI render correctly in
        // E2E mode. fetchMembershipsForUser swallows errors to [], so this
        // is safe even when the deployed env has no memberships yet.
        const list = await fetchMembershipsForUser(api)
        setMemberships(list)
        setUser({ ...parsedUser, memberships: list })
        await fetchProfile()
        setLoading(false)
        return
      }
      setLoading(false)
      return
    }

    // #181: refresh the platform-configured idle-session timeout before evaluating
    // it below, so an admin-changed value takes effect as soon as this tab reloads.
    // Public endpoint, no auth dependency; fails silently to the cached/default value.
    await fetchSessionTimeoutConfig()

    // #517: the in-tab inactivity logout is paused while the tab is closed, so a
    // returning user past the idle window would otherwise be silently restored by
    // the still-valid refresh cookie. If we recorded activity longer ago than the
    // session timeout, don't restore — clear the cookie and require a fresh login.
    if (isIdleExpired()) {
      try {
        await api.post('/auth/logout', {})
        // Only drop the persisted timestamp once the cookie is confirmed cleared.
        // If logout failed, keep it so this branch retries on the next load —
        // otherwise a transient failure would let the still-valid cookie silently
        // restore the session on the next reload (defeating #517).
        clearLastActivity()
      } catch (e) {
        console.debug('Idle-expiry cookie clear failed; will retry next load:', e)
      }
      setUser(null)
      setProfile(null)
      setMemberships([])
      idTokenRef.current = null
      setError(null)
      setLoading(false)
      return
    }

    try {
      // POST /auth/refresh — uses HttpOnly cookie; returns { user, accessToken, idToken }
      const response = await api.post('/auth/refresh', {})
      const { user: refreshedUser, accessToken, idToken } = response.data

      setAccessToken(idToken)
      cognitoAccessTokenRef.current = accessToken
      scheduleTokenRefresh(idToken)
      await adoptSession(refreshedUser, idToken)

      await fetchProfile()
    } catch (err) {
      // No valid session — user is not authenticated
      setUser(null)
      setProfile(null)
      setMemberships([])
      idTokenRef.current = null
      setError(null)
    } finally {
      setLoading(false)
    }
  }

  // ============================================
  // Login (password)
  // ============================================

  const login = async (email, password) => {
    try {
      setError(null)
      setIsAuthenticating(true)
      const normalizedEmail = email?.toLowerCase().trim()

      const response = await api.post('/auth/login', { email: normalizedEmail, password })
      const data = response.data

      // Check for auth challenge (MFA, new password required)
      if (data.challengeName) {
        // The pool signs in by email alias, so the Cognito username is the sub
        // (USER_ID_FOR_SRP). RespondToAuthChallenge needs that value as USERNAME —
        // passing the email alias makes Cognito reject the session (a confusing 401).
        const challengeUsername = data.challengeParameters?.USER_ID_FOR_SRP || normalizedEmail
        currentChallengeSession = { session: data.session, email: normalizedEmail, username: challengeUsername }

        if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setRequiresPasswordChange(true)
          setIsAuthenticating(false)
          return {
            success: false,
            requiresPasswordChange: true,
            challengeName: 'NEW_PASSWORD_REQUIRED',
            user: normalizedEmail,
          }
        }

        if (data.challengeName === 'SOFTWARE_TOKEN_MFA') {
          setRequiresMfaVerification(true)
          setMfaChallengeUser(normalizedEmail)
          setIsAuthenticating(false)
          return {
            success: false,
            requiresMfaVerification: true,
            challengeName: 'SOFTWARE_TOKEN_MFA',
            user: normalizedEmail,
          }
        }
      }

      // Successful login
      setAccessToken(data.idToken)
      cognitoAccessTokenRef.current = data.accessToken
      recordActivity() // #517: seed the idle timer so it survives a tab close
      scheduleTokenRefresh(data.idToken)
      await adoptSession(data.user, data.idToken)
      setRequiresPasswordChange(false)
      setIsAuthenticating(false)

      // Clear lockout on successful login (best-effort)
      try {
        await api.delete('/auth/lockout')
      } catch (e) {
        console.debug('Failed to clear lockout:', e)
      }

      await fetchProfile()
      return { success: true }
    } catch (err) {
      setError(err.message)
      setIsAuthenticating(false)
      return { success: false, error: err.message }
    }
  }

  // ============================================
  // Password change (NEW_PASSWORD_REQUIRED challenge)
  // ============================================

  const changePassword = async (_cognitoUser, newPassword) => {
    try {
      setError(null)
      setLoading(true)
      setIsAuthenticating(true)

      if (!currentChallengeSession) {
        throw new Error('No active challenge session. Please sign in again.')
      }

      const response = await api.post('/auth/login/challenge', {
        challengeName: 'NEW_PASSWORD_REQUIRED',
        challengeResponse: newPassword,
        session: currentChallengeSession.session,
        email: currentChallengeSession.email,
        username: currentChallengeSession.username,
      })

      const data = response.data
      if (data.user) {
        setAccessToken(data.idToken)
        cognitoAccessTokenRef.current = data.accessToken
        recordActivity() // #517: seed the idle timer so it survives a tab close
        scheduleTokenRefresh(data.idToken)
        await adoptSession(data.user, data.idToken)
        setRequiresPasswordChange(false)
        setIsAuthenticating(false)
        currentChallengeSession = null

        await fetchProfile()
        return { success: true }
      }

      setIsAuthenticating(false)
      return { success: false, error: 'Failed to complete password change' }
    } catch (err) {
      setError(err.message)
      setIsAuthenticating(false)
      return { success: false, error: err.message }
    } finally {
      setLoading(false)
    }
  }

  // ============================================
  // MFA verification (TOTP)
  // ============================================

  const verifyMfaCode = async (totpCode) => {
    try {
      setError(null)
      setIsAuthenticating(true)

      if (!currentChallengeSession) {
        throw new Error('No active MFA session. Please sign in again.')
      }

      const response = await api.post('/auth/login/challenge', {
        challengeName: 'SOFTWARE_TOKEN_MFA',
        challengeResponse: totpCode,
        session: currentChallengeSession.session,
        email: currentChallengeSession.email,
        username: currentChallengeSession.username,
      })

      const data = response.data
      if (data.user) {
        setAccessToken(data.idToken)
        cognitoAccessTokenRef.current = data.accessToken
        recordActivity() // #517: seed the idle timer so it survives a tab close
        scheduleTokenRefresh(data.idToken)
        await adoptSession(data.user, data.idToken)
        setRequiresMfaVerification(false)
        setMfaChallengeUser(null)
        setIsAuthenticating(false)
        currentChallengeSession = null

        await fetchProfile()
        return { success: true }
      }

      setIsAuthenticating(false)
      return { success: false, error: 'Failed to verify MFA code' }
    } catch (err) {
      setError(err.message)
      setIsAuthenticating(false)
      return { success: false, error: err.message }
    }
  }

  // ============================================
  // Recovery code (calls backend, not Cognito directly)
  // ============================================

  const verifyRecoveryCode = async (recoveryCode) => {
    try {
      setError(null)
      setIsAuthenticating(true)

      const response = await api.post('/users/mfa/recovery-login', {
        email: mfaChallengeUser,
        recoveryCode
      })

      if (response.data.success) {
        setRequiresMfaVerification(false)
        setMfaChallengeUser(null)
        setIsAuthenticating(false)
        return {
          success: true,
          message: response.data.message || 'Recovery code verified. MFA has been disabled. Please log in again.'
        }
      }

      setIsAuthenticating(false)
      return { success: false, error: response.data.error || 'Failed to verify recovery code' }
    } catch (err) {
      setError(err.response?.data?.error || err.message)
      setIsAuthenticating(false)
      return { success: false, error: err.response?.data?.error || err.message }
    }
  }

  const cancelMfaChallenge = async () => {
    currentChallengeSession = null
    setRequiresMfaVerification(false)
    setMfaChallengeUser(null)
    setIsAuthenticating(false)
    setError(null)
  }

  // ============================================
  // Logout
  // ============================================

  const logout = async () => {
    try {
      // POST /auth/logout — pass the Cognito access token in the body for GlobalSignOut.
      // The Bearer token in the Authorization header is now the ID token (for API Gateway),
      // so we pass the access token separately for server-side token revocation.
      await api.post('/auth/logout', { accessToken: cognitoAccessTokenRef.current })
    } catch (err) {
      console.error('Error during sign out:', err)
    } finally {
      setAccessToken(null)
      cognitoAccessTokenRef.current = null
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
      setUser(null)
      setProfile(null)
      setMemberships([])
      idTokenRef.current = null
      setRequiresPasswordChange(false)
      setRequiresMfaVerification(false)
      setMfaChallengeUser(null)
      setError(null)
      currentChallengeSession = null
      clearLastActivity() // #517: drop the persisted idle timestamp on logout
      queryClient.clear()
      sessionStorage.removeItem('coformit_mfa_session_check')
    }
  }

  // ============================================
  // Password reset (still uses Amplify SDK — separate issue)
  // ============================================

  const forgotPassword = async (email) => {
    try {
      setError(null)
      const normalizedEmail = email?.toLowerCase().trim()
      await resetPassword({ username: normalizedEmail })
      return { success: true }
    } catch (err) {
      setError(err.message)
      return { success: false, error: err.message }
    }
  }

  const confirmForgotPassword = async (email, code, newPassword) => {
    try {
      setError(null)
      const normalizedEmail = email?.toLowerCase().trim()
      await confirmResetPassword({
        username: normalizedEmail,
        confirmationCode: code,
        newPassword
      })
      return { success: true }
    } catch (err) {
      setError(err.message)
      return { success: false, error: err.message }
    }
  }

  // ============================================
  // Change password (authenticated user — uses Amplify for now)
  // ============================================

  const changeUserPassword = async (currentPassword, newPassword) => {
    try {
      setError(null)
      await updatePassword({ oldPassword: currentPassword, newPassword })
      return { success: true }
    } catch (err) {
      let errorMessage = err.message
      if (err.name === 'NotAuthorizedException') {
        errorMessage = 'Current password is incorrect'
      } else if (err.name === 'InvalidPasswordException') {
        errorMessage = 'New password does not meet requirements'
      } else if (err.name === 'LimitExceededException') {
        errorMessage = 'Too many attempts. Please try again later.'
      }
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  // ============================================
  // Get access token (for API calls that need it explicitly)
  // ============================================

  const getAuthToken = async () => {
    // Try in-memory token first
    const token = getAccessToken()
    if (token) return token
    // Fallback: try Amplify session (for changeUserPassword which still uses Amplify)
    try {
      const session = await fetchAuthSession()
      return session.tokens?.idToken?.toString() || null
    } catch {
      return null
    }
  }

  // ============================================
  // SSO callback handler
  // ============================================

  /**
   * Complete SSO login after OAuth callback.
   * Called by SsoCallbackPage with the auth code and PKCE verifier.
   */
  const completeSsoLogin = async (code, codeVerifier, redirectUri) => {
    try {
      setIsAuthenticating(true)

      const response = await api.post('/auth/sso/exchange', {
        code,
        codeVerifier,
        redirectUri,
      })

      const { user: ssoUser, accessToken, idToken } = response.data
      setAccessToken(idToken)
      cognitoAccessTokenRef.current = accessToken
      recordActivity() // #517: seed the idle timer so it survives a tab close
      scheduleTokenRefresh(idToken)
      await adoptSession(ssoUser, idToken)
      setIsAuthenticating(false)

      await fetchProfile()
      return { success: true }
    } catch (err) {
      console.error('SSO callback error:', err)
      setIsAuthenticating(false)
      return { success: false, error: err.message || 'SSO authentication failed' }
    }
  }

  /**
   * Force a token refresh and re-read tenant claims + memberships.
   *
   * Used by the TenantSwitcher (Task 16) after the backend re-issues an
   * idToken whose `custom:activeTenantId` claim points at a different tenant
   * — the UI needs to see the new claim immediately, not on the next page
   * load.
   *
   * Goes through our backend `/auth/refresh` proxy (which uses the HttpOnly
   * refresh cookie) so we stay consistent with the rest of the auth flow.
   * Falls back to the Amplify SDK if the proxy is unavailable.
   */
  const refreshSession = useCallback(async () => {
    try {
      const response = await api.post('/auth/refresh', {})
      const { user: refreshedUser, accessToken, idToken } = response.data
      setAccessToken(idToken)
      cognitoAccessTokenRef.current = accessToken
      scheduleTokenRefresh(idToken)
      const enriched = await adoptSession(refreshedUser, idToken)
      return { success: true, user: enriched }
    } catch (err) {
      // Fall back to Amplify forceRefresh — used by E2E mode and as a safety net.
      try {
        const session = await fetchAuthSession({ forceRefresh: true })
        const idToken = session?.tokens?.idToken?.toString() || null
        if (idToken) {
          setAccessToken(idToken)
          // Re-apply claims to whatever user we already have in state.
          idTokenRef.current = idToken
          const enriched = applyTenantClaimsToUser(user, idToken)
          const list = enriched ? await fetchMembershipsForUser(api) : []
          setMemberships(list)
          if (enriched) setUser({ ...enriched, memberships: list })
          return { success: true, user: enriched }
        }
      } catch (innerErr) {
        console.warn('refreshSession fallback failed:', innerErr?.message || innerErr)
      }
      return { success: false, error: err?.message || 'Failed to refresh session' }
    }
  }, [adoptSession, scheduleTokenRefresh, user])

  const capabilities = profile?.capabilities || []

  const hasCapability = useCallback((capability) => {
    const caps = profile?.capabilities || []
    return caps.includes(capability)
  }, [profile])

  const value = {
    user,
    profile,
    memberships,
    capabilities,
    hasCapability,
    loading,
    isAuthenticating,
    error,
    requiresPasswordChange,
    requiresMfaVerification,
    mfaChallengeUser,
    login,
    logout,
    changePassword,
    changeUserPassword,
    forgotPassword,
    confirmForgotPassword,
    verifyMfaCode,
    verifyRecoveryCode,
    cancelMfaChallenge,
    updateProfile,
    refreshProfile: fetchProfile,
    refreshSession,
    getCurrentUser: checkUser,
    getAuthToken,
    completeSsoLogin,
    // Legacy SSO handler kept for backwards compat — use completeSsoLogin instead
    handleSsoCallback: async () => ({ success: false, error: 'Use completeSsoLogin' }),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
